<template>
  <div class="content">
    <div class="row align-items-center mb-3">
      <div class="col-md-2 ms-3" align="left">
        <v-btn fab dark x-small color="green" @click="volver" title="Volver al listado">
          <v-icon dark>mdi-arrow-left</v-icon>
        </v-btn>
      </div>
    </div>

  <LayoutPage :title="`Detalle del Ticket${ticket ? ' #' + ticket.id : ''}`">
    <div class="ticket-detail">
      <!-- Loader mientras carga -->
      <div v-if="cargando && !ticket" class="text-center pa-5">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <template v-else-if="ticket">
        <!-- ============================================ -->
        <!--  Header: título + meta (solicitante, progreso, estado, categoría) -->
        <!-- ============================================ -->
        <div class="ticket-head">
          <div class="ticket-head__title">
            <span class="ticket-id-badge">#{{ ticket.id }}</span>
            <h2 class="ticket-subject">{{ ticket.asunto }}</h2>
          </div>

          <div class="ticket-head__meta">
            <!-- Solicitante -->
            <div class="meta-user" title="Solicitante">
              <v-avatar :color="clientColor" size="28" class="flex-shrink-0">
                <span class="white--text meta-user__initials">{{ inicialesCreador }}</span>
              </v-avatar>
              <div class="meta-user__data">
                <span class="meta-user__email">
                  {{ ticket.creator_email || ticket.creator_name || ticket.client_name || 'Sin usuario' }}
                </span>
              </div>
            </div>

            <span class="meta-sep"></span>

            <!-- Progreso (ticket.estado) -->
            <v-chip
              v-if="currentStatusOption"
              :color="currentStatusOption.color"
              text-color="white"
              small
              title="Progreso"
            >
              <v-icon small left>{{ currentStatusOption.icon }}</v-icon>
              {{ currentStatusOption.label }}
            </v-chip>
            <v-chip v-else color="grey" text-color="white" small title="Progreso">
              Sin progreso
            </v-chip>

            <!-- Estado (abierto / cerrado) -->
            <v-chip
              :color="ticket.abierta ? 'green' : 'red'"
              text-color="white"
              small
              title="Estado del ticket"
            >
              {{ ticket.abierta ? 'Abierto' : 'Cerrado' }}
            </v-chip>

            <!-- Categoría -->
            <v-chip
              v-if="ticket.categoria"
              color="#64748b"
              text-color="white"
              small
              title="Categoría"
            >
              <v-icon small left>mdi-tag-outline</v-icon>
              {{ ticket.categoria }}
            </v-chip>

            <span class="meta-date" title="Fecha de creación">
              <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
              {{ formatFecha(ticket.created_at) }}
            </span>
          </div>
        </div>

        <!-- ============================================ -->
        <!--  Descripción (colapsable, cerrada por defecto) -->
        <!-- ============================================ -->
        <div class="desc-panel">
          <button
            type="button"
            class="desc-toggle"
            :aria-expanded="detailOpen ? 'true' : 'false'"
            @click="detailOpen = !detailOpen"
          >
            <v-icon small class="mr-1">mdi-text-box-outline</v-icon>
            <span>Descripción</span>
            <v-chip v-if="attachments.length" x-small class="ml-2">
              <v-icon x-small left>mdi-paperclip</v-icon>{{ attachments.length }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-icon>{{ detailOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </button>

          <v-expand-transition>
            <div v-show="detailOpen" class="desc-body">
              <div class="ticket-query">{{ ticket.consulta }}</div>

              <div v-if="attachments.length > 0" class="attachments-section mt-3">
                <v-divider class="my-3"></v-divider>
                <div class="attachments-section__title mb-2">
                  <v-icon small class="mr-1">mdi-paperclip</v-icon>
                  <span class="text-capitalize">Archivos adjuntos ({{ attachments.length }})</span>
                </div>
                <div class="attachments-gallery">
                  <div
                    v-for="att in attachments"
                    :key="att.id"
                    class="attachment-item"
                    @click="handleAttachmentClick(att)"
                  >
                    <img
                      v-if="isImage(att.url, att.archivo)"
                      :src="att.thumb_url || att.url"
                      :alt="att.archivo"
                      class="attachment-image"
                    />
                    <span v-if="isImage(att.url, att.archivo)" class="ext-badge">
                      {{ fileExt(att.archivo) }}
                    </span>
                    <div v-else class="attachment-file">
                      <v-icon large :color="getFileColor(att.archivo)">{{ getFileIcon(att.archivo) }}</v-icon>
                      <span class="file-name" :title="att.archivo">{{ truncateFilename(att.archivo, 14) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </div>

        <!-- ============================================ -->
        <!--  Chat panel (conversación + composer)        -->
        <!-- ============================================ -->
        <v-card class="ticket-conversation-panel" elevation="2" style="display: flex; flex-direction: column;">
          <v-card-title 
            class="text-subtitle-1 grey lighten-2 py-2" 
            style="cursor: pointer" 
            @click="chatOpen = !chatOpen"
          >
            <v-icon class="mr-2">mdi-message-text-outline</v-icon>
            Conversación
            <v-chip x-small class="ml-2">{{ mensajesValidos.length }}</v-chip>
            <span
              class="ws-status"
              :class="`ws-status--${chatSocketStatus}`"
              :title="`WebSocket: ${chatSocketStatus}`"
            ></span>
            <v-spacer></v-spacer>
            <v-icon>{{ chatOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-card-title>

          <v-expand-transition>
            <div v-show="chatOpen" style="display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0;">
              <div ref="scrollContainer" class="ticket-chat ticket-chat--panel">
            <div
              v-if="mensajesValidos.length === 0"
              class="ticket-chat-empty"
            >
              <v-icon large color="grey lighten-1">mdi-comment-text-outline</v-icon>
              <span>Aún no hay respuestas. Sé el primero en responder.</span>
            </div>

            <div
              v-for="(msg, index) in mensajesValidos"
              :key="'cluster-' + index"
              class="chat-cluster"
            >
              <div
                v-if="shouldShowDaySeparator(msg, index)"
                class="day-separator"
              >
                <span>{{ dayLabel(msg.Fecha) }}</span>
              </div>

              <div
                class="ticket-chat-row"
                :class="{
                  'ticket-chat-row--mine': esMensajeMio(msg),
                  'ticket-chat-row--grouped': shouldGroupWithPrev(msg, index)
                }"
              >
                <v-avatar
                  v-if="!shouldGroupWithPrev(msg, index)"
                  :color="avatarColorMensaje(msg)"
                  size="32"
                  class="ticket-chat-avatar flex-shrink-0"
                >
                  <span class="white--text ticket-chat-initials">
                    {{ inicialesMensaje(msg) }}
                  </span>
                </v-avatar>
                <div v-else class="ticket-chat-avatar-spacer flex-shrink-0"></div>

                <div
                  class="ticket-chat-bubble"
                  :class="{
                    'ticket-chat-bubble--mine': esMensajeMio(msg),
                    'ticket-chat-bubble--grouped': shouldGroupWithPrev(msg, index)
                  }"
                >
                  <div v-if="!shouldGroupWithPrev(msg, index)" class="ticket-chat-meta">
                    <span class="ticket-chat-name">
                      {{ msg.Nombre || msg.Email || 'Usuario' }}
                    </span>
                    <span class="ticket-chat-time">
                      {{ formatHora(msg.Fecha) }}
                    </span>
                  </div>
                  <div class="ticket-chat-text">{{ msg.Respuesta }}</div>

                  <div
                    v-if="msg.Attachments && msg.Attachments.length"
                    class="ticket-chat-attachments"
                  >
                    <div
                      v-for="att in msg.Attachments"
                      :key="att.id || att.archivo || att.filename"
                      class="ticket-chat-attachment"
                      @click="handleAttachmentClick(att)"
                    >
                      <img
                        v-if="isImage(att.url, att.archivo)"
                        :src="att.thumb_url || att.url"
                        :alt="att.archivo"
                      />
                      <span v-else class="ticket-chat-attachment-file">
                        <v-icon small :color="getFileColor(att.archivo || att.filename)">{{ getFileIcon(att.archivo || att.filename) }}</v-icon>
                        <span :title="att.archivo || att.filename">{{ truncateFilename(att.archivo || att.filename, 12) }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="ticket-chat-panel-composer">
            <div v-if="archivoSeleccionado" class="file-preview d-flex align-center mb-2 pa-2 grey lighten-3 rounded">
              <img v-if="filePreview" :src="filePreview" alt="Preview" class="preview-image mr-2" />
              <div v-else class="preview-file mr-2">
                <v-icon :color="getFileColor(archivoSeleccionado.name)">{{ getFileIcon(archivoSeleccionado.name) }}</v-icon>
              </div>
              <span class="flex-grow-1 text-truncate">
                {{ truncateFilename(archivoSeleccionado.name, 36) }}
              </span>
              <v-btn icon small color="error" @click="clearFile">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <input
              ref="fileInput"
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              style="display: none"
              @change="handleFileSelect"
            />

            <div class="ticket-chat-composer">
              <button
                type="button"
                class="ticket-chat-composer-btn"
                title="Adjuntar archivo"
                @click="triggerFileInput"
              >
                <v-icon small>mdi-paperclip</v-icon>
              </button>
              <textarea
                v-model="nuevaRespuesta"
                class="ticket-chat-composer-textarea"
                placeholder="Escribí una respuesta..."
                rows="1"
                ref="textarea"
                @keydown.enter.exact.prevent="enviarRespuesta"
                @keydown.shift.enter="newLine"
                @input="autoresize"
              />
              <button
                type="button"
                class="ticket-chat-composer-btn ticket-chat-composer-btn--send"
                :disabled="!nuevaRespuesta.trim() || enviando"
                :title="enviando ? 'Enviando...' : 'Enviar respuesta'"
                @click="enviarRespuesta"
              >
                <v-icon small v-if="!enviando">mdi-send</v-icon>
                <v-icon small v-else>mdi-loading mdi-spin</v-icon>
              </button>
              </div>
            </div>
            </div>
          </v-expand-transition>
        </v-card>
      </template>

      <v-alert v-else type="warning" border="left" class="mt-4">
        No se pudo cargar el ticket.
        <v-btn text color="primary" @click="cargarTicket">Reintentar</v-btn>
      </v-alert>

      <!-- Dialog para ver imagen grande -->
      <v-dialog v-model="imagenDialog" max-width="90vw">
        <img :src="imagenUrl" alt="Imagen" style="width: 100%; cursor: pointer; border-radius: 8px" @click="imagenDialog = false" />
      </v-dialog>

      <!-- Snackbar de feedback -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
        {{ snackbarText }}
      </v-snackbar>
    </div>
  </LayoutPage>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import LayoutPage from "../../../components/LayoutPage.vue";
import ChatSocket from "../../../services/chatSocketSoportes.js";

export default {
  name: "TicketDetalle",
  components: { LayoutPage },
  data: () => ({
    cargando: false,
    detailOpen: true,
    chatOpen: true,
    ticket: null,
    itemConsultas: [],
    attachments: [],
    nuevaRespuesta: "",
    archivoSeleccionado: null,
    filePreview: null,
    enviando: false,
    imagenDialog: false,
    imagenUrl: "",
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
    chatSocket: null,
    chatSocketStatus: "idle",
    statusOptions: [
      { label: "En espera", value: "espera", icon: "mdi-clock-outline", color: "amber darken-2" },
      { label: "Resolviendo", value: "resolviendo", icon: "mdi-cog-outline", color: "blue darken-2" },
      { label: "Resuelta", value: "resuelta", icon: "mdi-check-circle", color: "green darken-2" },
      { label: "Rechazada", value: "rechazada", icon: "mdi-close-circle", color: "red darken-2" },
      { label: "Pendiente Desarrollo", value: "pendiente de desarrollo", icon: "mdi-pause-circle", color: "deep-purple darken-2" },
    ],
  }),

  computed: {
    ...mapGetters("soportes", ["getCurrentClientId"]),
    ...mapGetters(["getCurrentClientEmail"]),

    clientColor() {
      if (!this.ticket) return "#4caf50";
      const color = this.ticket.client_color || "4caf50";
      return color.startsWith("#") ? color : "#" + color;
    },

    categoryColor() {
      if (!this.ticket?.categoria) return "#64748b";
      return "#64748b";
    },

    categoryTextColor() {
      return "#ffffff";
    },

    currentStatusOption() {
      return this.statusOptions.find(o => o.value === this.ticket?.estado) || null;
    },

    inicialesCreador() {
      const t = this.ticket;
      if (!t) return "?";
      const nombre = t.creator_name || t.creator_email || t.client_name || "";
      return this.getIniciales(nombre);
    },

    mensajesValidos() {
      return (this.itemConsultas || []).filter(m => m && m.Respuesta);
    },
  },

  async created() {
    await this.cargarTicket();
  },

  beforeDestroy() {
    // Desconectar WebSocket al salir del componente
    this.disconnectChatSocket();
  },

  watch: {
    "$route.params.id"(newId) {
      if (newId) this.cargarTicket();
    },
    "itemConsultas.length"() {
      this.$nextTick(() => this.scrollContainerToBottom());
    },
  },

  methods: {
    ...mapActions("soportes", [
      "getSoporteDetail",
      "markSoporteRead",
      "createSoporteRespuesta",
      "getRealtimeToken",
    ]),

    formatFecha(fecha) {
      if (!fecha) return "";
      return moment(fecha).format("DD-MM-yyyy HH:mm");
    },

    getIniciales(nombre) {
      if (!nombre) return "?";
      const partes = String(nombre).trim().split(/\s+/);
      if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase();
      return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
    },

    getContrastColor(hex) {
      if (!hex) return "#ffffff";
      let h = hex.startsWith("#") ? hex.slice(1) : hex;
      if (h.length === 3) h = h.split("").map(c => c + c).join("");
      if (h.length !== 6) return "#ffffff";
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      const yiq = (r * 299 + g * 587 + b * 114) / 1000;
      return yiq >= 128 ? "#000000" : "#ffffff";
    },

    esMensajeMio(msg) {
      const myEmail = this.$store.state.email || this.$store.getters.getCurrentClientEmail;
      
      if (msg.Email && myEmail && msg.Email.toLowerCase() === myEmail.toLowerCase()) {
        return true;
      }
      
      return false;
    },

    avatarColorMensaje(msg) {
      return this.esMensajeMio(msg) ? "#1976d2" : this.clientColor;
    },

    inicialesMensaje(msg) {
      return this.getIniciales(msg?.Nombre || msg?.Email || "");
    },

    shouldShowDaySeparator(msg, index) {
      if (index === 0) return true;
      const prev = this.mensajesValidos[index - 1];
      if (!prev) return false;
      const prevDate = (prev.Fecha || "").substring(0, 10);
      const currDate = (msg.Fecha || "").substring(0, 10);
      return prevDate !== currDate;
    },

    shouldGroupWithPrev(msg, index) {
      if (index === 0) return false;
      const prev = this.mensajesValidos[index - 1];
      if (!prev) return false;
      // Mismo día
      const prevDate = (prev.Fecha || "").substring(0, 10);
      const currDate = (msg.Fecha || "").substring(0, 10);
      if (prevDate !== currDate) return false;
      // Mismo autor (email)
      const prevEmail = (prev.Email || "").toLowerCase();
      const currEmail = (msg.Email || "").toLowerCase();
      if (!prevEmail || prevEmail !== currEmail) return false;
      // Mismo minuto (HH:MM)
      const prevTime = (prev.Fecha || "").substring(11, 16);
      const currTime = (msg.Fecha || "").substring(11, 16);
      return prevTime === currTime;
    },

    dayLabel(fecha) {
      const dateStr = (fecha || "").substring(0, 10);
      if (!dateStr) return "";
      const [day, month, year] = dateStr.split("-");
      const date = new Date(`${year}-${month}-${day}`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.getTime() === today.getTime()) return "Hoy";
      if (date.getTime() === yesterday.getTime()) return "Ayer";
      return date.toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });
    },

    formatHora(fecha) {
      if (!fecha) return "";
      return fecha.substring(11, 16) || "";
    },

    newLine() {
      // Shift+Enter: nueva línea (manejado por el textarea)
    },

    autoresize(e) {
      const el = e?.target || this.$refs.textarea;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    },

    async cargarTicket() {
      this.cargando = true;
      this.ticket = null;
      this.itemConsultas = [];
      this.attachments = [];

      const ticketId = Number(this.$route.params.id);

      try {
        // El external_id (cliente) lo fija el back desde la sesión: acá solo
        // necesitamos el id del ticket. Pedimos miniaturas de las imágenes.
        const data = await this.getSoporteDetail({ ticketId, includeThumbs: true });

        this.ticket = data.ticket;
        this.attachments = data.attachments || [];
        this.itemConsultas = (data.responses || []).map((r) => ({
          Id: r.id,
          Email: r.user_email || r.creator_email || "",
          Nombre: r.user_name || r.creator_name || r.user_email || r.creator_email || "Usuario",
          Fecha: this.formatFecha(r.created_at),
          Respuesta: r.respuesta,
          Attachments: r.attachments || [],
          UserId: r.user_id || 0,
        }));

        // Marcar como leído
        try {
          await this.markSoporteRead({ ticketId });
          if (this.ticket) this.ticket.visto = true;
        } catch (e) {
          console.warn("No se pudo marcar como leído:", e);
        }

        // Conectar al WebSocket del chat (tiempo real)
        this.connectChatSocket(ticketId);
      } catch (error) {
        this.$toastr.e(error.userMessage || "No se pudo cargar el ticket.");
        console.error("Error cargando ticket:", error);
      } finally {
        this.cargando = false;
      }
    },

    // ==========================================
    //  WebSocket del chat (tiempo real)
    // ==========================================
    connectChatSocket(ticketId) {
      // Desconectar socket anterior si existe
      this.disconnectChatSocket()
      this.chatSocketStatus = "connecting"
      this.chatSocket = new ChatSocket({
        kind: "ticket",
        ticketId,
        // ws_token efímero pedido al backend (no expone la X-Integration-Key).
        getToken: () => this.getRealtimeToken(),
        onMessage: (data) => this.handleSocketMessage(data),
        onStatus: (status) => {
          this.chatSocketStatus = status
        },
      })
      this.chatSocket.connect()
    },

    disconnectChatSocket() {
      if (this.chatSocket) {
        this.chatSocket.close()
        this.chatSocket = null
      }
      this.chatSocketStatus = "idle"
    },

    handleSocketMessage(data) {
      if (!data || typeof data !== "object") return
      const type = data.type || data.event
      if (type === "response.created" || type === "response" || type === "message") {
        const resp = data.data || data.response || data.payload
        if (resp && (resp.respuesta || resp.text)) {
          this.addConsulta(resp)
          this.$nextTick(() => this.scrollContainerToBottom())
        }
      } else if (type === "tickets.changed" || type === "ticket.updated" || type === "status.changed") {
        // Recargar el ticket si el server avisa que algo cambió.
        this.cargarTicket()
      }
    },

    addConsulta(resp) {
      if (!resp || !resp.id) return
      // Evitar duplicados (por si llega por REST y por WS)
      if (this.itemConsultas.some((c) => c.Id === resp.id)) return
      this.itemConsultas.push(this.mapearRespuestaDelBack(resp))
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.archivoSeleccionado = file;
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.filePreview = e.target.result;
          };
          reader.readAsDataURL(file);
        } else {
          this.filePreview = null;
        }
      }
    },

    clearFile() {
      this.archivoSeleccionado = null;
      this.filePreview = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async enviarRespuesta() {
      if (!this.nuevaRespuesta.trim()) return;
      if (!this.ticket) return;

      this.enviando = true;
      try {
        let payload;
        if (this.archivoSeleccionado) {
          payload = new FormData();
          payload.append("soportesId", this.ticket.id);
          payload.append("respuesta", this.nuevaRespuesta);
          payload.append("file", this.archivoSeleccionado);
        } else {
          payload = {
            soportesId: this.ticket.id,
            respuesta: this.nuevaRespuesta,
          };
        }

        // El back devuelve la respuesta creada (con id, created_at, etc).
        // La usamos para pushear canónicamente sin recargar.
        const response = await this.createSoporteRespuesta(payload);

        // Pasar por addConsulta para que el dedup por id funcione
        // (evita duplicado cuando el WS broadcast llega antes/después del POST)
        this.addConsulta(response.data);
        this.nuevaRespuesta = "";
        this.clearFile();
        // this.$toastr.s("Respuesta enviada correctamente");

        // Scroll al final
        this.$nextTick(() => this.scrollContainerToBottom());
      } catch (error) {
        this.$toastr.e(error.userMessage || "No se pudo enviar la respuesta");
      } finally {
        this.enviando = false;
      }
    },

    // Mapea la respuesta cruda del back al formato que usa la timeline.
    mapearRespuestaDelBack(r) {
      if (!r) return null;
      return {
        Id: r.id, // preservar id para que addConsulta pueda dedup
        Email: r.user_email || r.creator_email || "",
        Nombre: r.user_name || r.creator_name || r.user_email || r.creator_email || "Usuario",
        Fecha: r.created_at
          ? moment(r.created_at).format("DD-MM-yyyy HH:mm:ss")
          : moment().format("DD-MM-yyyy HH:mm:ss"),
        Respuesta: r.respuesta || r.text || "",
        Attachments: r.attachments || [],
        UserId: r.user_id || 0,
      };
    },

    scrollContainerToBottom() {
      this.$nextTick(() => {
        const c = this.$refs.scrollContainer;
        if (c) c.scrollTop = c.scrollHeight;
      });
    },

    isImage(url, filename) {
      if (filename) {
        const ext = (filename.toLowerCase().split(".").pop() || "");
        return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
      }
      if (!url) return false;
      const exts = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
      return exts.some((e) => url.toLowerCase().endsWith(e));
    },

    fileExt(name) {
      if (!name) return "";
      const idx = name.lastIndexOf(".");
      return idx === -1 ? "" : name.slice(idx + 1).toUpperCase();
    },

    getFileIcon(name) {
      if (!name) return "mdi-file-outline";
      const ext = name.split(".").pop().toLowerCase();
      const map = {
        pdf: "mdi-file-pdf-box",
        doc: "mdi-file-word-box",
        docx: "mdi-file-word-box",
        xls: "mdi-file-excel-box",
        xlsx: "mdi-file-excel-box",
        csv: "mdi-file-excel-box",
        ppt: "mdi-file-powerpoint-box",
        pptx: "mdi-file-powerpoint-box",
        zip: "mdi-folder-zip-outline",
        rar: "mdi-folder-zip-outline",
        "7z": "mdi-folder-zip-outline",
        tar: "mdi-folder-zip-outline",
        gz: "mdi-folder-zip-outline",
        mp3: "mdi-file-music-outline",
        wav: "mdi-file-music-outline",
        ogg: "mdi-file-music-outline",
        mp4: "mdi-file-video-outline",
        avi: "mdi-file-video-outline",
        mkv: "mdi-file-video-outline",
        mov: "mdi-file-video-outline",
        txt: "mdi-file-document-outline",
        md: "mdi-language-markdown",
        log: "mdi-file-document-outline",
        json: "mdi-code-json",
        xml: "mdi-xml",
        html: "mdi-language-html5",
        css: "mdi-language-css3",
        js: "mdi-language-javascript",
      };
      return map[ext] || "mdi-file-outline";
    },

    getFileColor(name) {
      if (!name) return "grey darken-1";
      const ext = name.split(".").pop().toLowerCase();
      const map = {
        pdf: "red darken-2",
        doc: "blue darken-2",
        docx: "blue darken-2",
        xls: "green darken-2",
        xlsx: "green darken-2",
        csv: "green darken-2",
        ppt: "orange darken-2",
        pptx: "orange darken-2",
        zip: "amber darken-3",
        mp3: "purple darken-2",
        mp4: "indigo darken-2",
        html: "deep-orange darken-2",
        css: "blue darken-1",
        js: "yellow darken-3",
        json: "teal darken-1",
      };
      return map[ext] || "grey darken-1";
    },

    truncateFilename(name, maxLen = 18) {
      if (!name) return "";
      if (name.length <= maxLen) return name;
      const idx = name.lastIndexOf(".");
      if (idx === -1) return name.slice(0, maxLen - 3) + "...";
      const ext = name.slice(idx);
      const keep = maxLen - ext.length - 3;
      if (keep <= 0) return "..." + ext;
      return name.slice(0, keep) + "..." + ext;
    },

    handleAttachmentClick(att) {
      const url = att.url || att.archivo;
      if (this.isImage(url, att.archivo)) {
        this.imagenUrl = url;
        this.imagenDialog = true;
      } else if (url) {
        window.open(url, "_blank");
      }
    },

    mostrarSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    volver() {
      this.$router.push({ name: "Soportes" });
    },
  },
};
</script>

<style scoped>
.ticket-detail {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

/* ============================================ */
/*  Fit-in-viewport dentro del LayoutPage       */
/* ============================================ */
::v-deep .card-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

::v-deep .card {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

::v-deep .v-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ============================================ */
/*  Header: título + meta (solicitante/progreso/estado/categoría) */
/* ============================================ */
.ticket-head {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  flex: 0 0 auto;
}

.ticket-head__title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.ticket-id-badge {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1976d2;
  background: #e3f2fd;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.ticket-subject {
  font-size: 1.05rem;
  font-weight: 600;
  color: #212121;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ticket-head__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.meta-user {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.meta-user__initials {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.meta-user__data {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.meta-user__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.meta-user__email {
  font-size: 0.72rem;
  color: #9e9e9e;
  text-transform: none; /* el email NO se capitaliza */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.meta-sep {
  width: 1px;
  height: 24px;
  background: #e0e0e0;
  flex-shrink: 0;
}

.meta-date {
  color: #757575;
  font-size: 0.76rem;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
}

/* ============================================ */
/*  Descripción (colapsable, cerrada por defecto) */
/* ============================================ */
.desc-panel {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  flex: 0 0 auto;
  overflow: hidden;
}

.desc-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  background: #f7f8fa;
  border: none;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.desc-toggle:hover {
  background: #eef0f3;
}

.desc-body {
  padding: 12px 14px 14px;
}

.ticket-query {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #424242;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
  margin: 0;
}

.attachments-section__title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #616161;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
}

.attachments-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 6px;
}

.attachment-item {
  position: relative;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  background: #fafafa;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  text-align: center;
  width: 100%;
  min-width: 0;
}

.file-name {
  font-size: 0.68rem;
  margin-top: 0.25rem;
  width: 100%;
  padding: 0 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  line-height: 1.2;
  color: #616161;
}

.ext-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.65rem;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
}

/* (El sidebar read-only se reemplazó por la fila de meta del header.) */

/* ============================================ */
/*  Chat panel                                   */
/* ============================================ */
.ticket-conversation-panel {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
}

.ticket-conversation-panel .v-card__title {
  flex: none;
  border-bottom: 1px solid #e0e0e0;
  padding-top: 8px;
  padding-bottom: 8px;
}

/* Indicador estado WebSocket */
.ws-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  flex-shrink: 0;
  background: #9e9e9e;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}
.ws-status--connecting {
  background: #fb8c00;
  animation: wsPulse 1.2s ease-in-out infinite;
}
.ws-status--open {
  background: #43a047;
  box-shadow: 0 0 0 2px rgba(67, 160, 71, 0.25);
}
.ws-status--closed,
.ws-status--error,
.ws-status--idle {
  background: #9e9e9e;
}

@keyframes wsPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.ticket-chat {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background-color: #fafafa;
  overflow-y: auto;
  min-height: 0;
  max-height: 50vh;
}

.ticket-chat--panel {
  flex: 1 1 auto;
  max-height: 50vh;
}

.ticket-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: #9e9e9e;
  gap: 6px;
  font-size: 0.85rem;
}

/* Agrupa separador-de-día + fila del mensaje sin romper el layout flex del chat
   (display: contents hace que los hijos sean items directos de .ticket-chat).
   Permite poner la :key del v-for en este elemento y no en los hijos. */
.chat-cluster {
  display: contents;
}

.ticket-chat-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 85%;
}

.ticket-chat-row--mine {
  flex-direction: row-reverse;
  align-self: flex-end;
}

.ticket-chat-row--grouped {
  margin-top: -4px;
}

.ticket-chat-avatar {
  flex-shrink: 0;
}

.ticket-chat-avatar-spacer {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.ticket-chat-initials {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
}

.ticket-chat-bubble {
  background: #ffffff;
  padding: 8px 12px 6px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  min-width: 0;
  word-break: break-word;
  text-align: left;
}

.ticket-chat-bubble--grouped {
  padding-top: 4px;
  padding-bottom: 4px;
}

.ticket-chat-bubble--mine {
  background: #e3f2fd;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 12px;
}

.ticket-chat-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
  font-size: 0.75rem;
}

.ticket-chat-name {
  font-weight: 600;
  color: #1976d2;
}

.ticket-chat-time {
  color: #9e9e9e;
  font-weight: 400;
}

.ticket-chat-text {
  font-size: 0.875rem;
  color: #212121;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  margin: 0;
}

.ticket-chat-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.ticket-chat-attachment {
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  max-width: 200px;
  flex-shrink: 0;
  cursor: pointer;
}

.ticket-chat-attachment img {
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  display: block;
}

.ticket-chat-attachment-file {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #616161;
}

.day-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
  position: relative;
}

.day-separator span {
  background: #fafafa;
  padding: 0 12px;
  font-size: 0.7rem;
  color: #9e9e9e;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
}

.day-separator::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: #e0e0e0;
}

/* Composer */
.ticket-chat-panel-composer {
  flex: none;
  border-top: 1px solid #e0e0e0;
  padding: 10px 14px;
  background: #fff;
}

.ticket-chat-composer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  background: #f5f7fa;
  border: 1px solid #e6e8eb;
  border-radius: 24px;
  padding: 6px 6px 6px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.ticket-chat-composer:focus-within {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.12);
}

.ticket-chat-composer-textarea {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #212121;
  max-height: 120px;
  min-height: 24px;
  padding: 6px 0;
}

.ticket-chat-composer-textarea::placeholder {
  color: #9e9e9e;
}

.ticket-chat-composer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #757575;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s;
  padding: 0;
}

.ticket-chat-composer-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
  color: #212121;
}

.ticket-chat-composer-btn--send {
  background: #1976d2;
  color: #fff;
}

.ticket-chat-composer-btn--send:hover:not(:disabled) {
  background: #1565c0;
  color: #fff;
}

.ticket-chat-composer-btn--send:disabled {
  background: #bbdefb;
  color: #fff;
  cursor: not-allowed;
}

.file-preview {
  margin-bottom: 6px;
}

.preview-image {
  max-width: 40px;
  max-height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.preview-file {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  font-size: 1.4rem;
}

.ticket-chat-attachment-file {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  max-width: 100%;
  min-width: 0;
}

.ticket-chat-attachment-file span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
}

/* Responsive */
@media (max-width: 700px) {
  .ticket-head {
    padding: 8px 10px;
  }
  .ticket-subject {
    font-size: 0.95rem;
    white-space: normal;
  }
  .meta-date {
    margin-left: 0;
  }
  .ticket-chat-row {
    max-width: 92%;
  }
}
</style>
